import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardBody, CardFooter, CardHeader } from 'components/common';
import { ApiUserPermissions, PermissionKey, UserPermissions } from 'store';
import { Namespace } from 'enums';

import { PermissionItem } from './PermissionItem';
import { EditorButtons } from './EditorButtons';
import styles from './PermissionEditor.module.scss';

const permissionsKeys = ['hasManageCompensationsPermission', 'hasAdministratorPermission'];

interface Props {
  userPermissions: ApiUserPermissions;
  savePermission: (privileges: ApiUserPermissions) => void;
}

export const PermissionEditor = ({ userPermissions, savePermission }: Props) => {
  const { t: tSettings } = useTranslation(Namespace.Settings);
  const [checkboxPermissions, setCheckboxPermissions] = useState<Record<string, boolean>>();
  const [permissions, setPermissions] = useState<UserPermissions>();
  const [isEditMode, setEditMode] = useState(false);

  useEffect(() => {
    if (userPermissions) {
      setEditMode(false);
      setPermissions(new UserPermissions(userPermissions.privileges));
    }
  }, [userPermissions]);

  useEffect(() => {
    if (permissions) {
      const newCheckboxPermissions = permissionsKeys.reduce(
        (accumulator, item) => ({
          ...accumulator,
          [item]: permissions[item as keyof UserPermissions],
        }),
        {},
      );

      setCheckboxPermissions(newCheckboxPermissions);
    }
  }, [permissions]);

  const handleEditClick = useCallback(() => {
    setEditMode(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    setEditMode(false);

    if (permissions) {
      setPermissions(new UserPermissions(permissions.savedPermissionsNumber));
    }
  }, [permissions]);

  const handleClickPermission = useCallback(
    (permissionKey: string) => (checked: boolean) => {
      setCheckboxPermissions((permissionsState) => ({
        ...permissionsState,
        [permissionKey]: checked,
      }));

      if (permissions) {
        permissions[permissionKey as PermissionKey] = checked;
      }
    },
    [permissions],
  );

  const handleSaveClick = useCallback(() => {
    if (permissions) {
      if (permissions.permissionsNumber !== permissions.savedPermissionsNumber) {
        savePermission({
          ...userPermissions,
          privileges: permissions.permissionsNumber,
        });
      } else {
        setEditMode(false);
      }
    }
  }, [savePermission, userPermissions, permissions]);

  return (
    <Card className={styles.card}>
      <CardHeader className={styles.cardHeader}>
        <div className={styles.headerItem}>
          <div className={styles.label}>{userPermissions.isGroup ? tSettings('adGroup') : tSettings('username')}</div>
          <div className={styles.value}>{userPermissions.displayName}</div>
        </div>
        {!userPermissions.isGroup && (
          <div className={styles.headerItem}>
            <div className={styles.label}>{tSettings('userLogin')}</div>
            <div className={styles.value}>{userPermissions.login}</div>
          </div>
        )}
      </CardHeader>
      <CardBody>
        {checkboxPermissions && (
          <>
            {permissionsKeys.map((permissionKey) => (
              <PermissionItem
                key={permissionKey}
                label={tSettings(permissionKey)}
                value={checkboxPermissions[permissionKey]}
                isEdit={isEditMode}
                onChange={handleClickPermission(permissionKey)}
              />
            ))}
          </>
        )}
      </CardBody>
      <CardFooter>
        <EditorButtons
          isEditMode={isEditMode}
          onCancelClick={handleCancelClick}
          onEditClick={handleEditClick}
          onSaveClick={handleSaveClick}
          editLabel={tSettings('editAccess')}
          disabledSave={permissions?.permissionsNumber === permissions?.savedPermissionsNumber}
        />
      </CardFooter>
    </Card>
  );
};
